import PropTypes from 'prop-types';
import { Box, Button } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import ActionButton from '../../components/ActionButton';
import { Table } from '../../components/table/Table';
import { IrrigationEditorDialog } from './IrrigationEditorDialog';
import { useGrowers } from '../../hooks/use-growers';
import { useMapRefresh } from '../../components/google/maps/use-map-refresh';
import * as irrigationService from '../../services/irrigation-service';
import { useAlerts } from '../../hooks/use-alerts';
import LocalDate from '../../components/LocalDate';
import { Acres } from '../../components/Acres';
import { useContext } from './context';
import ConfirmationDialog from '../../components/ConfirmationDialog/ConfirmationDialog';

IrrigationTab.propTypes = {
  isActive: PropTypes.bool.isRequired,
  renderTabs: PropTypes.func.isRequired,
};

export function IrrigationTab(props) {
  const { isActive } = props;
  const [boundaryToEdit, setBoundaryToEdit] = useState();
  const [boundaryToDelete, setBoundaryToDelete] = useState();
  const [working, setWorking] = useState(false);
  const { selectedId: growerId } = useGrowers();
  const { refresh } = useMapRefresh();
  const { showError } = useAlerts();
  const {
    selectedFeatures,
    setSelectedFeatures,
    focusedFeature,
    setFocusedFeature,
    irrigationBoundaries,
    setIrrigationBoundaries,
  } = useContext();

  const selectedBoundary = useMemo(() => {
    if (selectedFeatures?.length !== 1) return null;
    return irrigationBoundaries.find((b) => b.id === selectedFeatures[0]);
  }, [irrigationBoundaries, selectedFeatures]);

  const onRowClick = useCallback(
    (row) =>
      focusedFeature === row.id
        ? setFocusedFeature(null)
        : setFocusedFeature(row.id),
    [focusedFeature, setFocusedFeature],
  );

  useEffect(() => {
    if (irrigationBoundaries !== undefined) return;

    setIrrigationBoundaries(null);

    irrigationService
      .getBoundaries(growerId)
      .then(setIrrigationBoundaries)
      .catch(showError);
  }, [
    irrigationBoundaries,
    growerId,
    isActive,
    showError,
    setIrrigationBoundaries,
  ]);

  if (!props.isActive) return null;

  return (
    <>
      {buildEditor()}
      {buildDelete()}
      {buildHeader()}
      {buildTable()}
    </>
  );

  function buildHeader() {
    return (
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <Box sx={{ flex: 1 }}>{props.renderTabs()}</Box>
        <Box sx={{ mr: 1 }}>
          <ActionButton>
            <Button onClick={() => setBoundaryToEdit({})}>Add New</Button>
            <Button
              onClick={() => setBoundaryToEdit(selectedBoundary)}
              disabled={!selectedBoundary}
            >
              Edit Boundary
            </Button>
            <Button
              onClick={() => setBoundaryToDelete(selectedBoundary)}
              disabled={!selectedBoundary}
            >
              Delete
            </Button>
          </ActionButton>
        </Box>
      </Box>
    );
  }

  function buildEditor() {
    return boundaryToEdit ? (
      <IrrigationEditorDialog
        growerId={growerId}
        close={closeEditor}
        boundary={boundaryToEdit}
      />
    ) : null;
  }

  function buildDelete() {
    return boundaryToDelete ? (
      <ConfirmationDialog
        title="Delete Irrigation Boundary?"
        message="Are you sure you want to delete this irrigation boundary?"
        confirming={working}
        handleClose={() => setBoundaryToDelete(null)}
        handleConfirm={deleteSelectedBoundary}
      />
    ) : null;
  }

  function deleteSelectedBoundary() {
    setWorking(true);
    irrigationService
      .destroyBoundary(growerId, boundaryToDelete)
      .then(() => {
        setIrrigationBoundaries((current) =>
          current.filter((b) => b.id !== boundaryToDelete.id),
        );
        refresh();
        setBoundaryToDelete(null);
      })
      .finally(() => setWorking(false));
  }

  function closeEditor(newBoundary, oldBoundary) {
    setBoundaryToEdit(null);

    if (!newBoundary) return;

    setIrrigationBoundaries((current) => [
      ...current.filter((b) => b.id !== oldBoundary.id),
      newBoundary,
    ]);
    refresh();
  }

  function buildTable() {
    return (
      <Box
        sx={{
          overflow: 'auto',
          height: '100%',
        }}
      >
        <Table
          checkboxSelection
          disableRowSelectionOnClick
          rowSelectionModel={selectedFeatures}
          onRowSelectionModelChange={setSelectedFeatures}
          focusedRow={focusedFeature}
          hideFooter={false}
          columns={buildColumns()}
          rows={irrigationBoundaries || []}
          onRowClick={onRowClick}
          loading={!irrigationBoundaries}
          localeText={{
            noRowsLabel: 'No irrigation boundaries',
            footerTotalRows: 'Total:',
            footerRowSelected: (count) => `${count.toLocaleString()} selected`,
          }}
          initialState={{
            sorting: {
              sortModel: [{ field: 'intersectingFields', sort: 'asc' }],
            },
          }}
        />
      </Box>
    );
  }

  function buildColumns() {
    return [
      {
        field: 'intersectingFields',
        headerName: 'Intersecting Fields',
        flex: 2,
      },
      {
        field: 'acres',
        headerName: 'Acres',
        flex: 1,
        renderCell: ({ row }) => <Acres acres={row.acres} />,
      },
      {
        field: 'startsAt',
        headerName: 'Created On',
        type: 'date',
        valueGetter: ({ value }) => value && new Date(value),
        renderCell: ({ row }) => <LocalDate value={row.startsAt} />,
        flex: 1,
      },
      {
        field: 'createdBy',
        headerName: 'Created By',
        flex: 1,
      },
    ];
  }
}
